import { c as _c } from "react/compiler-runtime";
import { BaseButton } from '@7f-ui/components/button/button';
import { Modal } from '@7f-ui/components/modal/modal';
import { RichText } from '@7f-ui/components/rich-text/rich-text';
import { useResizeObserverRef } from '@7f-ui/hooks/use-resize-observer-ref';
import type { NexperienceFragment } from '@data-access/lib/gql/experience/documents';
import { Star } from '@phosphor-icons/react';
import { shopTestId } from '@utils/utils-shop-test-ids';
import DOMPurify from 'isomorphic-dompurify';
import { useEffect, useRef, useState } from 'react';
import { sanitizeBackendHtml } from '@/lib/security/sanitize-html';
export function AppExperienceHighlights(t0) {
  const $ = _c(25);
  const {
    experience
  } = t0;
  const [anchor, setAnchor] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const hasAdditionalDescription = !!DOMPurify.sanitize(experience.additionalDescriptionHtml ?? "", {
    RETURN_DOM_FRAGMENT: true
  }).textContent?.trim();
  const [isDescriptionOverflowing, setIsDescriptionOverflowing] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = t2 => {
      const [entry] = t2;
      setIsDescriptionOverflowing(entry.target.scrollHeight > entry.target.clientHeight);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [descriptionPreviewRef] = useResizeObserverRef(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = nextAnchor => {
      setShowModal(true);
      setAnchor(nextAnchor);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleButtonClick = t2;
  let t3;
  if ($[2] !== experience.highlights) {
    t3 = experience.highlights ?? [];
    $[2] = experience.highlights;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = t3.slice(0, 5);
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = <div className="mb-4"><HighlightsList highlights={t4} /></div>;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== experience.highlights) {
    t6 = !!experience.highlights?.length && experience.highlights.length > 5 && <BaseButton className="font-body-small font-semibold text-link" onClick={() => handleButtonClick("highlights")}>Alle Highlights</BaseButton>;
    $[8] = experience.highlights;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== descriptionPreviewRef || $[11] !== experience.descriptionHtml || $[12] !== hasAdditionalDescription || $[13] !== isDescriptionOverflowing) {
    t7 = experience.descriptionHtml && <><div ref={descriptionPreviewRef} data-testid={shopTestId("ExperienceHighlights.description")} className="mt-8 line-clamp-3"><RichText text={sanitizeBackendHtml(experience.descriptionHtml)} /></div>{(isDescriptionOverflowing || hasAdditionalDescription) && <BaseButton className="mt-4 font-body-small font-semibold text-link" data-testid={shopTestId("ExperienceHighlights.readDescription")} onClick={() => handleButtonClick("description")}>Mehr erfahren</BaseButton>}</>;
    $[10] = descriptionPreviewRef;
    $[11] = experience.descriptionHtml;
    $[12] = hasAdditionalDescription;
    $[13] = isDescriptionOverflowing;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setShowModal(false);
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== anchor || $[17] !== experience || $[18] !== showModal) {
    t9 = <HighlightsModal anchor={anchor} experience={experience} open={showModal} onClose={t8} />;
    $[16] = anchor;
    $[17] = experience;
    $[18] = showModal;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t5 || $[21] !== t6 || $[22] !== t7 || $[23] !== t9) {
    t10 = <>{t5}{t6}{t7}{t9}</>;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  return t10;
}
function HighlightsList(t0) {
  const $ = _c(4);
  const {
    highlights
  } = t0;
  let t1;
  if ($[0] !== highlights) {
    t1 = highlights.map(_temp);
    $[0] = highlights;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <ul className="space-y-4">{t1}</ul>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp(highlight, index) {
  return <li key={index} className="flex items-center space-x-3"><Star className="h-10 w-10 shrink-0 rounded-full border-8 border-white bg-lightgreen p-1 text-primary sm:size-12" /><p>{highlight}</p></li>;
}
function HighlightsModal(t0) {
  const $ = _c(23);
  const {
    anchor,
    experience,
    open,
    onClose
  } = t0;
  const descriptionRef = useRef(null);
  const highlightsRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== anchor) {
    t1 = () => {
      if (!anchor) {
        return;
      }
      bb5: switch (anchor) {
        case "highlights":
          {
            highlightsRef.current?.scrollIntoView({
              behavior: "smooth"
            });
            break bb5;
          }
        case "description":
          {
            descriptionRef.current?.scrollIntoView({
              behavior: "smooth"
            });
          }
      }
    };
    t2 = [anchor];
    $[0] = anchor;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== onClose) {
    t3 = <Modal.Header onClose={onClose}>Highlights</Modal.Header>;
    $[3] = onClose;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== experience.highlights) {
    t4 = experience.highlights ?? [];
    $[5] = experience.highlights;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <div className="mb-8" ref={highlightsRef}><HighlightsList highlights={t4} /></div>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== experience.additionalDescriptionHtml || $[10] !== experience.descriptionHtml) {
    t6 = experience.descriptionHtml && <div ref={descriptionRef}><RichText text={sanitizeBackendHtml(experience.descriptionHtml)} />{experience.additionalDescriptionHtml && <RichText text={sanitizeBackendHtml(experience.additionalDescriptionHtml)} className="mt-4" />}</div>;
    $[9] = experience.additionalDescriptionHtml;
    $[10] = experience.descriptionHtml;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5 || $[13] !== t6) {
    t7 = <Modal.Content>{t5}{t6}</Modal.Content>;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== onClose) {
    t8 = <Modal.Footer><Modal.Footer.Actions><Modal.Footer.Button full={true} onClick={onClose}>Schließen</Modal.Footer.Button></Modal.Footer.Actions></Modal.Footer>;
    $[15] = onClose;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== onClose || $[18] !== open || $[19] !== t3 || $[20] !== t7 || $[21] !== t8) {
    t9 = <Modal isOpen={open} onClose={onClose}>{t3}{t7}{t8}</Modal>;
    $[17] = onClose;
    $[18] = open;
    $[19] = t3;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
}